import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Customers from '@/pages/Customers';
import Prospects from '@/pages/Prospects';
import Portfolio from '@/pages/Portfolio';
import Products from '@/pages/Products';
import RepresentativeProducts from '@/pages/RepresentativeProducts';
import Targets from '@/pages/Targets';
import TargetAchievement from '@/pages/TargetAchievement';
import CustomerDetail from '@/pages/CustomerDetail';
import Offers from '@/pages/Offers';
import SubscriptionOffers from '@/pages/SubscriptionOffers';
import Visits from '@/pages/Visits';
import Calls from '@/pages/Calls';
import Sales from '@/pages/Sales';
import Users from '@/pages/Users';
import Reports from '@/pages/Reports';
import CustomerSalesReport from '@/pages/CustomerSalesReport';
import ProductBasedReports from '@/pages/ProductBasedReports';
import SalesQuantityReports from '@/pages/SalesQuantityReports';
// AI Analytics devre dışı bırakıldı
import SubscriptionSearches from '@/pages/searches/SubscriptionSearches';
import PortfolioSearches from '@/pages/searches/PortfolioSearches';
import WelcomeSearches from '@/pages/searches/WelcomeSearches';
import GeneralCalls from '@/pages/GeneralCalls';
import ProductLibrary from '@/pages/ProductLibrary';
import TotalRevenue from '@/pages/TotalRevenue';
import SubscriptionCaseList from '@/pages/SubscriptionCaseList';
import SubscriptionManagement from '@/pages/SubscriptionManagement';
import SubscriptionImport from '@/pages/SubscriptionImport';
import Layout from '@/components/Layout';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Toaster } from 'sonner';
import { ActionHistoryProvider } from '@/context/ActionHistoryContext';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">Yükleniyor...</div>;
  }

  return (
    <>
      <ErrorBoundary>
        <Toaster position="top-right" richColors />
        <PWAInstallPrompt />
        <ActionHistoryProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={
                isAuthenticated ? <Navigate to="/" /> : <Login onLogin={handleLogin} />
              } />
              
              <Route path="/" element={
                isAuthenticated ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />
              }>
                <Route index element={<Dashboard user={user} />} />
                <Route path="prospects" element={<Prospects user={user} />} />
                <Route path="prospects/:prospectId" element={<CustomerDetail user={user} isProspect={true} />} />
                <Route path="customers" element={<Customers user={user} />} />
                <Route path="customers/:customerId" element={<CustomerDetail user={user} />} />
                {(user?.role === 'temsilci' || user?.role === 'abonelik') && (
                  <Route path="portfolio" element={<Portfolio user={user} />} />
                )}
                <Route path="visits" element={<Visits user={user} />} />
                <Route path="calls" element={<Calls user={user} />} />
                <Route path="sales" element={<Sales user={user} />} />
                <Route path="offers" element={<Offers user={user} />} />
                <Route path="reports" element={<Reports user={user} />} />
                <Route path="customer-sales-report" element={<CustomerSalesReport user={user} />} />
                <Route path="product-based-reports" element={<ProductBasedReports user={user} />} />
                <Route path="sales-quantity-reports" element={<SalesQuantityReports user={user} />} />
                <Route path="target-achievement" element={<TargetAchievement user={user} />} />
                {user?.role === 'admin' && (
                  <>
                    <Route path="users" element={<Users user={user} />} />
                    <Route path="products" element={<Products user={user} />} />
                    <Route path="representative-products" element={<RepresentativeProducts user={user} />} />
                    <Route path="targets" element={<Targets user={user} />} />
                    {/* AI Analytics devre dışı bırakıldı */}
                    <Route path="searches/general" element={<GeneralCalls user={user} />} />
                    <Route path="total-revenue" element={<TotalRevenue user={user} />} />
                  </>
                )}
                {(user?.role === 'temsilci' || user?.role === 'abonelik') && (
                  <Route path="representative-products" element={<RepresentativeProducts user={user} />} />
                )}
                {(user?.role === 'admin' || user?.role === 'abonelik') && (
                  <>
                    <Route path="searches/subscription" element={<SubscriptionSearches user={user} />} />
                    <Route path="searches/portfolio" element={<PortfolioSearches user={user} />} />
                    <Route path="searches/welcome" element={<WelcomeSearches user={user} />} />
                    <Route path="subscription-offers" element={<SubscriptionOffers user={user} />} />
                    <Route path="subscription-management" element={<SubscriptionCaseList user={user} />} />
                    <Route path="subscription-management/import" element={<SubscriptionImport user={user} />} />
                    <Route path="subscription-management/:caseId" element={<SubscriptionManagement user={user} />} />
                  </>
                )}
                <Route path="product-library" element={<ProductLibrary user={user} />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </ActionHistoryProvider>
      </ErrorBoundary>
    </>
  );
}

export default App;